import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import React from "react";
import {
  DatePicker,
  FormGroup,
  Input,
  SubmitButton,
} from "../../../../@coherent/react-hook-form";
import { TextQuestion } from "../../components/TextQuestion";
import { TextareaQuestion } from "../../components/TextareaQuestion";
import moment from "moment";
import { get } from "lodash";
import { isEmail } from "validator";
import { useTranslation } from "react-i18next";

export const Step1Form = ({ required = true }) => {
  const { errors } = useFormContext();
  const { t } = useTranslation();
  const youngHelper =
    useWatch({
      name: "young_helper",
    }) === "Yes";

  return (
    <>
      {!!required ? (
        <>
          <p>
            Welcome to Make Lunch! We are so happy that you are applying to be
            part of the team. Ensuring clubs operate safely is one of our core
            values here at TLG Make Lunch so it is important that all volunteers
            are safely recruited. Please provide as much information as possible
            on the application form giving full answers as to why you want to
            volunteer and what experience you have.
          </p>
          <p>Please complete all sections of this application form.</p>
          <p>
            TLG are committed to keeping data secure, please visit{" "}
            <a href="https://www.tlg.org.uk/other/privacy-policy/volunteer-programmes-privacy-notice">
              https://www.tlg.org.uk/other/privacy-policy/volunteer-programmes-privacy-notice
            </a>{" "}
            for more information.
          </p>
          <p>
            We anticipate that this form should take you between 20 – 40 mins to
            complete. Your progress will be saved after each step if you need to
            come back to it later.
          </p>
          <hr />
        </>
      ) : null}
      <h3 className="tw-text-lg mb-4">Your Details</h3>
      <FormGroup
        name="young_helper"
        label="Are you applying to volunteer with your Make Lunch club as a Young Helper?"
      >
        <Controller
          name="young_helper"
          defaultValue=""
          rules={{ required }}
          render={(field) => (
            <select className="form-control" {...field}>
              <option value="" disabled />
              <option value="No">No, I am over 18 years old</option>
              <option value="Yes">Yes, I am under 18 years old</option>
            </select>
          )}
        />
      </FormGroup>
      <TextQuestion name="first_name" label="First Name" required={required} />
      <TextQuestion name="last_name" label="Last Name" required={required} />
      <TextQuestion
        name="email"
        label="Email Address"
        required={required}
        validate={(v) => isEmail(v)}
      />
      {!youngHelper && (
        <TextQuestion name="aliases" label={`Any former/"known as" names?"`} />
      )}

      <FormGroup
        name="date_of_birth"
        label="Date of birth"
        required={required}
        help={
          <>
            {get(errors, "date_of_birth.type") === "ofAge" && (
              <>{errors.date_of_birth.message}</>
            )}
          </>
        }
      >
        <DatePicker
          rules={{
            required: required,
            validate: {
              ofAge: (v) => {
                if (!required) return true;
                if (!v) return false;
                const d = moment.duration(moment().startOf("day") - moment(v));

                if (!youngHelper) {
                  return (
                    d.years() >= 18 ||
                    "The selected date of birth is under 18 years old."
                  );
                } else {
                  return (
                    (d.years() >= 11 && d.years() < 18) ||
                    "Young Helpers must be between 11 and 17 years old."
                  );
                }
              },
            },
          }}
          name="date_of_birth"
          className="form-control"
          defaultValue={null}
        />
      </FormGroup>
      <FormGroup label={t("Address")} required>
        <FormGroup name="address_1" className="mb-1">
          <Input
            className="form-control"
            name="address_1"
            rules={{
              required: required,
            }}
            placeholder={t("Address 1 *")}
          />
        </FormGroup>
        <FormGroup name="address_2" className="mb-1">
          <Input
            className="form-control"
            name="address_2"
            placeholder={t("Address 2")}
          />
        </FormGroup>
        <FormGroup name="address_3" className="mb-1">
          <Input
            className="form-control"
            name="address_3"
            placeholder={t("Address 3")}
          />
        </FormGroup>
        <FormGroup name="town" className="mb-1">
          <Input className="form-control" name="town" placeholder={t("Town")} />
        </FormGroup>
        <FormGroup name="county" className="mb-1">
          <Input
            className="form-control"
            name="county"
            placeholder={t("County")}
          />
        </FormGroup>
        <FormGroup name="postcode" className="mb-1">
          <Input
            className="form-control"
            name="postcode"
            placeholder={t("Postcode")}
          />
        </FormGroup>
      </FormGroup>
      <TextQuestion name="mobile" required={required} label="Mobile" />
      {!youngHelper && <TextQuestion name="phone" label="Telephone" />}
      <TextareaQuestion
        help="Minimum of 25 words"
        minWords={25}
        required={required}
        name="reason"
        label={<>Why would you like to join our Make Lunch team?</>}
      />
      <TextareaQuestion
        help="Minimum of 25 words"
        minWords={25}
        required={required}
        name="skills_or_interests"
        label={
          <>
            Do you have any skills or interests that you are hoping to put to
            use at Make Lunch e.g. cooking, cleaning, preparing activities,
            running games, connecting with parents, making people feel welcome
            etc?
          </>
        }
      />
      {!youngHelper && (
        <TextareaQuestion
          help="Minimum of 25 words"
          minWords={25}
          required={required}
          name="past_experience"
          label={
            <>
              Do you have any current or past experience of working with
              children and young people or families, whether paid or voluntary?
            </>
          }
        />
      )}
      <TextareaQuestion
        name="consistency_details"
        label={
          <>
            Please give us an idea of how frequently you anticipate you will be
            available to volunteer?
          </>
        }
      />
    </>
  );
};

export const Step1 = ({ onSubmit, defaultValues }) => {
  const methods = useForm({
    mode: "all",
    defaultValues,
    shouldUnregister: true,
  });
  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Step1Form />
        <div className="tw-flex tw-justify-end">
          <SubmitButton>Continue</SubmitButton>
        </div>
      </form>
    </FormProvider>
  );
};
